import { useEffect, useRef, useState } from 'react';

import { tracking } from '@iwoca/frontend-tracking-library';

import DocumentRequirementStep from './DocumentRequirementStep/DocumentRequirementStep';
import LoanSummaryRevamp from './LoanSummaryRevamp';
import AgedDebtorReport from './RequirementTypes/AgedDebtorReport';
import BankStatements from './RequirementTypes/BankStatements';
import BusinessPlan from './RequirementTypes/BusinessPlan';
import CompanyAccounts from './RequirementTypes/CompanyAccounts';
import ManagementAccounts from './RequirementTypes/ManagementAccounts';
import VATReturns from './RequirementTypes/VATReturns';
import styles from './TradingHistoryRevamp.module.css';
import {
  useGetApplicationQuery,
  useGetDocuments,
  TGetDocumentsResponse,
  TDocuments,
} from '../../../api/lendingApi/lendingApiQueries';
import { getDurationInMonths } from '../../../helpers/product.helpers';
import { useContextStateKey } from '../../../hooks/useContextStateKey';
import { useNumberFormat } from '../../../hooks/useNumberFormat';
import { useOffersUK } from '../../../hooks/useOffers/useOffersUK';
import { getAmplitudeDefaultEventProperties } from '../../../utils/amplitudeEvents';
import { Button } from '../../Button/Button';
import { ProgressBar } from '../ProgressBar/ProgressBar';
import { TradingHistoryLoader } from '../TradingHistoryLoader';
import {
  requirementTitles,
  requirementErrorMessages,
} from './RequirementTitles.helper';
import { LoanSummaryLoader } from '../TradingHistoryUK/LoanSummaryLoader';

const DOCUMENT_REQUIREMENTS: Record<string, React.ElementType> = {
  aged_debtor_report: AgedDebtorReport,
  bank_statement: BankStatements,
  business_plan: BusinessPlan,
  company_accounts: CompanyAccounts,
  management_accounts: ManagementAccounts,
  vat_return: VATReturns,
};

const REQUIREMENT_TYPE_TO_DOCUMENT_TYPE: Record<string, string> = {
  aged_debtor_report: 'aged_debtor_report',
  bank_statement: 'bank statement',
  business_plan: 'business_plan',
  company_accounts: 'business accounts',
  management_accounts: 'management_accounts',
  vat_return: 'vat return',
};

const VALID_DOCUMENT_STATUSES = ['complete', 'pending', 'under_review'];

const TradingHistoryRevamp = () => {
  const { formatCurrency } = useNumberFormat();
  const [activeStepIndex, setActiveStepIndex] = useState(0);
  const [attemptedSteps, setAttemptedSteps] = useState<Set<number>>(new Set());
  const [directUploadStepIndex, setDirectUploadStepIndex] = useState<
    number | null
  >(null);
  const [showSummary, setShowSummary] = useState(false);
  const didInitialAutoAdvance = useRef(false);

  const { application, isApplicationLoading } = useGetApplicationQuery();
  const stateKey = useContextStateKey();
  const { documents, isLoading, isRefetching, refetch } =
    useGetDocuments(stateKey);

  const {
    documentRequirements,
    isLoadingOffers,
    offers,
    handleDecision,
    isDeciding,
    isRefetchingOffers,
  } = useOffersUK({
    enabled: true,
  });

  const requiredDocumentRequirements =
    documentRequirements?.filter((doc) => !doc.is_optional) || [];

  const findFirstIncompleteStepIndex = () =>
    requiredDocumentRequirements.findIndex(
      (requirement) => !isStepComplete(requirement, documents),
    );

  // Refetch documents on mount to ensure fresh data
  useEffect(() => {
    if (stateKey) {
      refetch();
    }
  }, [stateKey, refetch]);

  // Set active step to first incomplete step on initial load only
  useEffect(() => {
    // Wait for data to be ready (both documents AND offers)
    if (
      !documents?.data ||
      isLoading ||
      isRefetching ||
      isLoadingOffers ||
      requiredDocumentRequirements.length === 0
    ) {
      return;
    }

    // Only run once - if already run, exit immediately
    if (didInitialAutoAdvance.current) return;

    // Find and set the first incomplete step
    const firstIncompleteIndex = findFirstIncompleteStepIndex();
    if (firstIncompleteIndex !== -1) {
      setActiveStepIndex(firstIncompleteIndex);
    } else if (requiredDocumentRequirements.length > 0) {
      // All steps complete - show summary
      setShowSummary(true);
    }

    // Mark as complete so this never runs again
    didInitialAutoAdvance.current = true;
  }, [documents, isLoading, isRefetching, isLoadingOffers]);

  useEffect(() => {
    tracking.ampli.stepLoaded({
      'Step name': 'Trading history',
      ...getAmplitudeDefaultEventProperties(),
    });
  }, []);

  const onSubmit = async () => {
    tracking.ampli.tradingHistorySubmitted({
      'Page version': 'v3',
      ...getAmplitudeDefaultEventProperties(),
    });
    await handleDecision();
  };

  const scrollToRequirement = (requirementType: string) => {
    const elementId = `document-requirement-${requirementType}`;
    const element = document.getElementById(elementId);
    element?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  };

  const handleContinueToNextStep = (
    currentIndex: number,
    requirement: (typeof requiredDocumentRequirements)[number],
    isLastStep: boolean,
  ) => {
    setAttemptedSteps((prev) => new Set(prev).add(currentIndex));

    if (!isStepComplete(requirement, documents)) {
      scrollToRequirement(requirement.type);
      return;
    }

    if (isLastStep) {
      setShowSummary(true);
    } else {
      setActiveStepIndex(currentIndex + 1);
    }
    setDirectUploadStepIndex(null);
  };

  const isStepComplete = (
    requirement: (typeof requiredDocumentRequirements)[number],
    currentDocuments: TGetDocumentsResponse | undefined,
  ) => {
    if (!currentDocuments?.data) return false;

    const documentType = REQUIREMENT_TYPE_TO_DOCUMENT_TYPE[requirement.type];
    if (!documentType) return false;

    // For bank_statement, check if a bank account has been linked via open banking,
    // or if documents have been uploaded (indicated by `pending` status).
    // The requirement is only marked `complete` later during Document Review,
    // so for this signup step we treat `pending` as step completion.
    if (requirement.type === 'bank_statement') {
      const openBankingResolution = requirement.resolution_methods.find(
        (resolution) =>
          resolution.type === 'link_bank_account_via_open_banking',
      );
      const hasLinkedAccount =
        openBankingResolution && openBankingResolution.items.length > 0;
      if (hasLinkedAccount || requirement.status === 'pending') {
        return true;
      }
    }

    const matchingDocuments = currentDocuments.data.filter(
      (doc: TDocuments) => doc.attributes.document_type === documentType,
    );

    return matchingDocuments.some((doc: TDocuments) =>
      VALID_DOCUMENT_STATUSES.includes(doc.attributes.status),
    );
  };

  // Calculate display amount and loan details from offers
  const requestedAmount = application?.requested_products?.[0]?.amount;
  const suggestedOffer = offers?.find((offer) => offer.is_suggested);
  const estimatedAmount = suggestedOffer?.max_amount;
  const maxTermLength = getDurationInMonths(suggestedOffer?.duration);

  const isEstimatedAmountSmaller =
    Number(estimatedAmount) < Number(requestedAmount);
  const displayAmount = isEstimatedAmountSmaller
    ? requestedAmount
    : estimatedAmount;

  if (isLoadingOffers || isLoading || isApplicationLoading) {
    return (
      <div className={styles.container}>
        <ProgressBar />
        <div className={styles.content}>
          <LoanSummaryLoader />
          <TradingHistoryLoader />
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <ProgressBar />
      <div className={styles.content}>
        <LoanSummaryRevamp
          displayAmount={displayAmount}
          maxTermLength={maxTermLength}
          isEstimatedAmountSmaller={isEstimatedAmountSmaller}
        />
        <h1 className={styles.heading}>
          You’re eligible for up to{' '}
          <span className={styles.amount}>
            {formatCurrency(displayAmount)}*
          </span>
        </h1>
        <p className={styles.subheading}>
          We just need a little bit more information to be able to make the most
          accurate assessment.
        </p>
        {requiredDocumentRequirements.length > 0 && (
          <div className={styles.steps}>
            {requiredDocumentRequirements.map((requirement, index) => {
              const RequirementContent =
                DOCUMENT_REQUIREMENTS[requirement.type];
              if (!RequirementContent) return null;

              const title = requirementTitles(
                requirement.type,
                requirement.quantifier?.value,
              );
              if (!title) return null;

              const isLastStep =
                index === requiredDocumentRequirements.length - 1;
              const isActive = !showSummary && index === activeStepIndex;
              const isComplete = isStepComplete(requirement, documents);
              const isError = attemptedSteps.has(index) && !isComplete;

              const handleUploadFurther = () => {
                setShowSummary(false);
                setActiveStepIndex(index);
                setDirectUploadStepIndex(index);
              };

              const showDirectUpload = directUploadStepIndex === index;

              return (
                <DocumentRequirementStep
                  key={requirement.type}
                  stepNumber={index + 1}
                  title={title}
                  isComplete={isComplete}
                  isActive={isActive}
                  isError={isError}
                  isLastStep={isLastStep}
                  errorMessage={requirementErrorMessages[requirement.type]}
                  showDirectUpload={showDirectUpload}
                  onContinue={() =>
                    handleContinueToNextStep(index, requirement, isLastStep)
                  }
                  onUploadFurther={handleUploadFurther}
                >
                  {({
                    isActive: stepIsActive,
                    isError: stepIsError,
                    onRequestUpload,
                    showDirectUpload: stepShowDirectUpload,
                  }) =>
                    requirement.type === 'bank_statement' ? (
                      <RequirementContent
                        resolutionMethods={requirement.resolution_methods}
                        isActive={stepIsActive}
                        isError={stepIsError}
                        onRequestUpload={onRequestUpload}
                        showDirectUpload={stepShowDirectUpload}
                      />
                    ) : (
                      <RequirementContent
                        isActive={stepIsActive}
                        isError={stepIsError}
                        onRequestUpload={onRequestUpload}
                      />
                    )
                  }
                </DocumentRequirementStep>
              );
            })}
          </div>
        )}
        {showSummary && (
          <div className={styles.submitSection}>
            <Button
              className={styles.submitButton}
              loading={isDeciding || isRefetchingOffers}
              onClick={onSubmit}
            >
              Submit application
            </Button>
            <p className={styles.submitText}>
              This will submit your application for review
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default TradingHistoryRevamp;
